/**
 * MembersStrip — public "who's in this Clan" social proof on ClanFeed.
 *
 * Reads `/api/creators/{handle}/members` (public, no auth, already existed).
 * Renders a compact horizontal strip of overlapping avatars for the top
 * holders + a total count. Hides itself entirely when there are zero
 * members (fresh clans or below the min_bits_to_join threshold) — social
 * proof that shows "nobody yet" is anti-proof.
 *
 * This appears on the non-member preview area to give unauthed / non-holder
 * visitors an at-a-glance sense of who's already invested in the community.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { resolveAvatarUrl } from '../api';

const MAX_AVATARS = 8;

// Medal for the top 3 holders — subtle recognition without a leaderboard.
// null for ranks 4+ so the top-3 highlight stays exclusive.
const RANK_MEDAL = ['🥇', '🥈', '🥉'];

const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return v.toLocaleString();
};

export default function MembersStrip({ clanHandle }) {
  const [members, setMembers] = useState(null); // null=loading, []=empty, [...]=has
  const [totalHolders, setTotalHolders] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!clanHandle) return undefined;
    // Fetch top 20 so we can show 8 + accurate "+N more"; the endpoint
    // caps at min_bits_to_join anyway so this is already filtered to
    // real members, not sub-threshold speculators.
    api.get(`/creators/${clanHandle}/members?limit=20`)
      .then(({ data }) => {
        if (cancelled) return;
        const ms = data?.members || [];
        setMembers(ms);
        setTotalHolders(ms.length);
      })
      .catch(() => { if (!cancelled) { setMembers([]); setTotalHolders(0); } });
    return () => { cancelled = true; };
  }, [clanHandle]);

  if (members === null) return null;
  if (members.length === 0) return null;

  const visible = members.slice(0, MAX_AVATARS);
  const extra = Math.max(0, totalHolders - MAX_AVATARS);
  const topBits = visible[0]?.bits_owned || 0;

  return (
    <div
      data-testid="members-strip"
      style={{
        marginTop: 12, marginBottom: 8,
        padding: 14, borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(0,180,255,0.06))',
        border: '1px solid rgba(124,58,237,0.30)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, opacity: 0.85, marginBottom: 10 }}>
        <span aria-hidden="true">👥</span>
        <span>{totalHolders} {totalHolders === 1 ? 'holder' : 'holders'} · already in this Clan</span>
        <span
          style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 999,
            background: 'rgba(180,255,0,0.15)', color: '#B4FF00',
            border: '1px solid rgba(180,255,0,0.4)', fontWeight: 700,
          }}
        >
          top holder · {fmtCompact(topBits)} Bits
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        {visible.map((m, idx) => {
          const grad = Array.isArray(m.gradient) && m.gradient.length >= 2
            ? m.gradient : ['#8B5CF6', '#F2679E'];
          const avatarSrc = m.avatar ? resolveAvatarUrl(m.avatar) : null;
          const medal = RANK_MEDAL[idx] || null;
          // Members without a handle can't route to /creator/:handle — fall back
          // to a non-clickable div so we don't produce a broken 404 link.
          const canLink = !!m.handle;
          const AvatarTag = canLink ? Link : 'div';
          const linkProps = canLink ? { to: `/creator/${m.handle}` } : {};
          return (
            <AvatarTag
              key={m.user_id}
              {...linkProps}
              data-testid={`members-strip-avatar-${idx}`}
              title={`${medal ? `#${idx + 1} · ` : ''}${m.display_name} · ${fmtCompact(m.bits_owned)} Bits${canLink ? ' · tap to view profile' : ''}`}
              aria-label={canLink ? `View ${m.display_name}'s profile` : m.display_name}
              style={{
                width: 34, height: 34, borderRadius: '50%',
                background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'visible', border: '2px solid #0f1218',
                marginLeft: idx === 0 ? 0 : -10, zIndex: MAX_AVATARS - idx,
                flexShrink: 0, position: 'relative',
                cursor: canLink ? 'pointer' : 'default',
                textDecoration: 'none',
                transition: 'transform 150ms ease',
              }}
              onMouseEnter={canLink ? (e) => { e.currentTarget.style.transform = 'scale(1.10)'; e.currentTarget.style.zIndex = 99; } : undefined}
              onMouseLeave={canLink ? (e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.zIndex = MAX_AVATARS - idx; } : undefined}
            >
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden' }}>
                {avatarSrc
                  ? <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  : <span style={{ fontWeight: 800, color: 'rgba(0,0,0,0.55)', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>{m.initials}</span>}
              </div>
              {medal && (
                <span
                  data-testid={`members-strip-medal-${idx}`}
                  aria-label={`Rank ${idx + 1}`}
                  style={{
                    position: 'absolute', bottom: -4, right: -4,
                    fontSize: 13, lineHeight: 1,
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                    pointerEvents: 'none',
                  }}
                >
                  {medal}
                </span>
              )}
            </AvatarTag>
          );
        })}
        {extra > 0 && (
          <div
            data-testid="members-strip-more"
            style={{
              width: 34, height: 34, borderRadius: '50%',
              background: 'rgba(255,255,255,0.10)', border: '2px solid #0f1218',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginLeft: -10, fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.85)',
              flexShrink: 0,
            }}
            title={`${extra} more member${extra === 1 ? '' : 's'}`}
          >
            +{extra}
          </div>
        )}
        <span style={{ marginLeft: 12, fontSize: 12, opacity: 0.7, whiteSpace: 'nowrap' }}>
          {visible[0]?.display_name || 'a holder'}{extra > 0 ? `, +${extra} others` : visible.length > 1 ? `, +${visible.length - 1} more` : ''}
        </span>
      </div>
    </div>
  );
}
